import { useState, useEffect } from "react";
import { Container, ListGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import "../sass/layout/quickAccess/quickAccess.css";
import QuickAccessItem from "./quickaccess/quickaccessitem";
import { Navbar, Col } from "react-bootstrap";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import MMALogo from "../utils/img/MMA-logo.png";
import AccordionProfile from "../components/accordion/accordion";
import UserService from "../services/api/UserService";
import { fetchComponentQuickAccess, getDropdowns, getButtonsInHome, getNewsletter, getTokenMedia, getAllMedicareNewsletters } from '../services/actions/home.action';
import RetireePortalButton from './RetireePortalButton';
import FlipBookModal from './flipbook/FlipBookModal';
import { trackMedicareNewsletterClick } from './views/newsletter';

const QuickAccess = ({ isMobile = false }) => {
  // const loginStatus = useSelector((state) => state.userReducer.loginStatus);
  const loginStatus = UserService.isLoggedIn();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [newsletterExpanded, setNewsletterExpanded] = useState(false);
  const [showFlipBook, setShowFlipBook] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const isBasePath = () =>
    !!(window.location.pathname === "/" || !id || id === "/" || !location);

  const isRetireesPage = window.location.pathname.includes("/retirees");

  const { location, myUserInfo, quickAccessLinks, buttons, newsletter, tokenMedia, allMedicareNewsletters } = useSelector((state) => ({
    quickAccessLinks: state.homeReducer.quickaccess,
    location: state.homeReducer.location?.data,
    myUserInfo: state.homeReducer.myUserInfo,
    buttons: state.homeReducer.buttons,
    newsletter: state.homeReducer.newsletter,
    tokenMedia: state.homeReducer.tokenMedia,
    allMedicareNewsletters: state.homeReducer.allMedicareNewsletters,
  }));

  const id = location?.uniqueNonGuessableId;

  useEffect(() => {
    if (id) {
      fetchComponentQuickAccess(dispatch, id);
      if (loginStatus && myUserInfo?.location) {
        getDropdowns(dispatch, myUserInfo?.location);
      }
    }
  }, [dispatch, id, loginStatus, myUserInfo]);

  useEffect(() => {
    const fetchNewsletterData = async () => {
      if (isRetireesPage) {
        // Fetch fresh access token first
        await getTokenMedia(dispatch);
        // Then fetch newsletter data
        await getNewsletter(dispatch, 'retireePortals');
        // Fetch all Medicare newsletters for the list
        await getAllMedicareNewsletters(dispatch);
      }
    };

    fetchNewsletterData();
  }, [dispatch, isRetireesPage]);

  const getNavigationPath = () => {
    if (currentLocation.pathname.includes('/retirees')) {
      return '/retirees';
    }
    return id !== "/" ? `/${id ?? ""}` : "/";
  };

  const getBlueCrossButton = () => {
    return buttons?.find(button =>
      button.name === "Blue Cross Blue Shield Benefits"
    );
  };

  const handleMedicareNewsletterClick = (newsletterData) => {
    if (isRetireesPage) {
      trackMedicareNewsletterClick(newsletterData, location);
      setSelectedNewsletter(newsletterData);
      setShowFlipBook(true);
    }
  };

  return (
    <Container bsPrefix="container" className="quickAcces-container">
      <Col className="container-title-sutitle">
        <Navbar.Brand>
          <NavLink to={getNavigationPath()} className="path-item">
            <h1 className="navbar-title-header">MIIA</h1>
          </NavLink>
        </Navbar.Brand>
        <Navbar.Brand>
          <Link to={getNavigationPath()} className="path-item">
            <h5 className="navbar-subtitle-logo qa-logo">
              {currentLocation.pathname.includes('/retirees') ? t("homepage.retireeportal") : t("homepage.memberportal")}
            </h5>
          </Link>
        </Navbar.Brand>
      </Col>
      <div className="divisor-line">-</div>
      <>
        <div className="quickAccess-list-container">
          {loginStatus ? (
            <>
              <AccordionProfile />
            </>
          ) : (
            <>



              {isBasePath() ? (
                <ListGroup className="quickAccess-list-group">
                  {isRetireesPage ? (
                    <>
                      {/* Contacts Section */}
                      <Link to='/retirees/contactMIIAteam'>
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-section-header">
                          {t("homepage.contacts")}
                        </ListGroup.Item>
                      </Link>

                      {/* Quick Links Section */}
                      <ListGroup.Item className="quickAccess-list-group-item quickAccess-section-header quickAccess-section-title">
                        {t("homepage.quickLinks")}
                      </ListGroup.Item>
                      {getBlueCrossButton()?.link && (
                        getBlueCrossButton().type === "internal_link" ? (
                          <Link to={`/retirees/${getBlueCrossButton().link}`}>
                            <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                              {t("homepage.eap")}
                            </ListGroup.Item>
                          </Link>
                        ) : (
                          <a href={getBlueCrossButton().link} target="_blank" rel="noopener noreferrer">
                            <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                              {t("homepage.eap")}
                            </ListGroup.Item>
                          </a>
                        )
                      )}
                      <a href="https://www.mychart.org/" target="_blank" rel="noreferrer">
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.findYourHealthPortal")}
                        </ListGroup.Item>
                      </a>
                      <a href="https://www.bluecrossma.org/myblue" target="_blank" rel="noreferrer">
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.myBlueInstantAccess")}
                        </ListGroup.Item>
                      </a>

                      {/* Newsletter Section - Expandable */}
                      {allMedicareNewsletters?.length > 0 && (
                        <>
                          <ListGroup.Item
                            className="quickAccess-list-group-item quickAccess-section-header quickAccess-expandable"
                            onClick={() => setNewsletterExpanded(!newsletterExpanded)}
                          >
                            The MIIA Health Benefits Trust Retiree Review Newsletter
                            <span className={`quickAccess-arrow ${newsletterExpanded ? 'expanded' : ''}`}>›</span>
                          </ListGroup.Item>
                          {newsletterExpanded && (
                            <>
                              {allMedicareNewsletters.map((newsletter) => (
                                <div
                                  key={newsletter.id}
                                  onClick={() => handleMedicareNewsletterClick(newsletter)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                                    {newsletter.attributes?.titleForLeftMenu}
                                  </ListGroup.Item>
                                </div>
                              ))}
                            </>
                          )}
                        </>
                      )}

                      {/* Health & Wellness Section */}
                      <ListGroup.Item className="quickAccess-list-group-item quickAccess-section-header quickAccess-section-title">
                        {t("homepage.healthWellness")}
                      </ListGroup.Item>
                      <Link to='/retirees/articleslist'>
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.medicareArticles")}
                        </ListGroup.Item>
                      </Link>
                      <Link to='/retirees/wellnessevents'>
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.medicareClasses")}
                        </ListGroup.Item>
                      </Link>
                      <Link to='/retirees/programslist'>
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.medicarePrograms")}
                        </ListGroup.Item>
                      </Link>
                      <Link to='/retirees/recipeslist'>
                        <ListGroup.Item className="quickAccess-list-group-item quickAccess-nested-item">
                          {t("homepage.medicareRecipes")}
                        </ListGroup.Item>
                      </Link>

                    </>
                  ) : (
                    <>
                      <Link to='/recipeslist'>
                        <ListGroup.Item className="quickAccess-list-group-item">
                          {t("homepage.recipes")}
                        </ListGroup.Item>
                      </Link>
                      <Link to='/articleslist'>
                        <ListGroup.Item className="quickAccess-list-group-item">
                          {t("homepage.articles")}
                        </ListGroup.Item>
                      </Link>
                      <a href="https://www.emiia.org/contact">
                        <ListGroup.Item className="quickAccess-list-group-item">
                          {t("homepage.contact2")}
                        </ListGroup.Item>
                      </a>
                    </>
                  )}
                </ListGroup>
              ) : (
                quickAccessLinks?.length > 0 && (
                  <>
                    <h5 className="quickAccess-title">Quick Access</h5>
                    <ul className="quickAccess-ul">
                      {quickAccessLinks.map((itemLink, index) => {
                        return (
                          <QuickAccessItem
                            key={index}
                            isMobile={isMobile}
                            itemquickacces={itemLink}
                          />
                        );
                      })}
                    </ul>
                  </>
                )
              )}
            </>
          )}
        </div>
        <div className="ultimate-items">
          <RetireePortalButton />
          <div className="text-bottom-quickAccess">
            <h4
              style={{
                width: "max-content",
                padding: "0 5px",
                color: "#8F003C",
              }}
            >
              MIIA
            </h4>
            <label
              style={{
                fontSize: "0.8rem",
                width: "max-content",
                padding: "0 0px",
              }}
            >
              {t("homepage.membership")}
            </label>
            <img className="mma-logo" src={MMALogo} />
          </div>
        </div>
      </>

      {isRetireesPage && (
        <FlipBookModal
          show={showFlipBook}
          handleClose={() => {
            setShowFlipBook(false);
            setSelectedNewsletter(null);
          }}
          pdfUrl={`${selectedNewsletter?.attributes?.pdf?.data?.attributes?.url || newsletter?.pdfUrl}?${tokenMedia}`}
          width="100%"
          height="80vh"
          className="retiree-newsletter-flipbook"
        />
      )}
    </Container>
  );
};

export default QuickAccess;
